<script lang="ts" setup>
import { computed, PropType, ref } from "vue";
import { useRouter } from "vue-router";
import { TirPmMultiselect, PopoverPositionType, TirPmSvg } from "tir-components";
import { ComponentInstance, debounce } from "tir-pm-toolkit";
import { QueueRelationModel } from "../../models";
import { ListBulletIcon } from "tir-style-system/icons/outline";
import TaskQueueSearchDropdownOption from "./task-queue-search-dropdown-option.vue";

/**Маршруты */
const router = useRouter();

/**Параметры */
const props = defineProps({
  /**Отключить поповер */
  isDisabled: { type: Boolean, default: false },
  /**Кастомная позиция поповера */
  customPopoverPosition: { type: Object as PropType<PopoverPositionType> },
  /**Не телепортировать */
  noTeleport: { type: Boolean, default: false },
  /**Отключение потери фокуса при клике по контенту */
  disableContentBlur: { type: Boolean, default: false },
  /**Закрыть при выборе */
  closeBySelect: { type: Boolean, default: false },
  /**Функция поиска */
  searchFunction: { type: Function },
  /**ID очереди где расположена задача */
  taskQueueId: { type: String, required: true },
});
/**События */
const emit = defineEmits<{
  /**Выбор очереди */
  onSelect: [queue: QueueRelationModel];
}>();

/**Поисковой запрос */
const search = ref("");
/**Список очередей */
const queues = ref<QueueRelationModel[]>([]);
/**Загрузка */
const isLoading = ref(false);
/**Кол-во очередей */
const queuesCount = ref();
/**Ссылка на компонента мультиселекта */
const $multiselect = ref<ComponentInstance<typeof TirPmMultiselect>>();

/**Запрос */
const pageRequest = computed(() => ({
  Search: search.value,
  Skip: queues.value.length,
  Take: 10,
}));
/**Id проекта */
const projectId = computed(() => Number(router.currentRoute.value.params.projectId));

const debounceOnSearch = debounce(() => searchQueues(true), 400);
/**Поиск очередей */
async function searchQueues(withResest?: boolean) {
  isLoading.value = true;
  if (withResest) {
    queues.value = [];
    pageRequest.value.Skip = 0;
  }
  if (
    queuesCount.value != undefined &&
    queuesCount.value !== 0 &&
    pageRequest.value.Skip >= queuesCount.value
  ) {
    isLoading.value = false;
    return;
  }

  const response = await props.searchFunction(projectId.value, pageRequest.value);

  if (response.IsSuccess) {
    queues.value.push(...response.Items);
    queuesCount.value = response.Count;
  }
  isLoading.value = false;
}
/**Проверить проскроллен ли элемент до конца */
const checkScrolledToBottom = (element: HTMLElement) => {
  return element.scrollTop + element.clientHeight + 100 >= element.scrollHeight;
};
/**При скролле */
const onScoll = (event: any) => {
  const target = event.target as HTMLElement | undefined;
  if (
    !target ||
    !checkScrolledToBottom(target) ||
    isLoading.value ||
    pageRequest.value.Skip >= queuesCount.value ||
    isLoading.value
  )
    return;

  searchQueues();
};
/**Обновление поиска */
const updateSearch = (_search: string) => {
  search.value = _search;
  debounceOnSearch();
};
/**Сброс значений переменных */
const resetData = () => {
  search.value = "";
  queues.value = [];
  queuesCount.value = undefined;
};
/**Выбор очереди */
const selectQueue = (_queue: QueueRelationModel) => {
  if (_queue.ParentTask) return;
  emit("onSelect", _queue);
  $multiselect.value?.close();
};
</script>
<template>
  <TirPmMultiselect
    no-teleport
    disable-content-blur
    search-input-autofocus
    staticScrollHeight
    class="task-queue-search-dropdown"
    ref="$multiselect"
    search-input-label="Поиск по очереди или рабочей области"
    popover-width="32.5rem"
    :is-search-local="false"
    :isDisabled="isDisabled"
    :options="queues"
    :popover-position="customPopoverPosition"
    show-on-hover
    @on-scroll="onScoll"
    @update-search="updateSearch"
    @enter="searchQueues"
    @on-open="searchQueues"
    @on-close="resetData"
  >
    <template #target>
      <slot name="target" />
    </template>
    <template #optionList="{ option }">
      <TaskQueueSearchDropdownOption
        :queue="option"
        :is-current-queue="taskQueueId === option.Id"
        @select="selectQueue(option)"
      />
    </template>
  </TirPmMultiselect>
</template>
<style lang="scss" scoped>
.task-queue-search-dropdown {
  &_item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 3rem;
    padding: 0 0.75rem;
    transition: 0.2s linear;
    cursor: pointer;

    &:hover {
      background: var(--neutral-translucent-5);
    }

    & > div {
      display: flex;
      gap: 0.25rem;
      max-width: 50%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 0.875rem;
      font-weight: 500;

      svg {
        min-width: 1rem;
        min-height: 1rem;
        max-width: 1rem;
        max-height: 1rem;
      }
    }

    &_queue {
      color: var(--text-primary, #272d37);
    }

    &_namespace {
      display: flex;
      align-items: center;
      color: var(--text-secondary, #686c73);
    }

    &_namespace-icon {
      font-size: 1.5rem;
    }
  }
}
</style>
<style lang="scss">
.task-queue-search-dropdown_item.disabled {
  background: var(--bg-base) !important;
  cursor: default;
  .task-queue-search-dropdown_item_queue,
  .task-queue-search-dropdown_item_namespace {
    color: var(--text-tertiary) !important;
  }
}
</style>
